import { useState, useEffect, useMemo } from 'react';
import { Box, TextField } from '@mui/material';
import { useController } from 'react-hook-form';
import { IdType } from 'react-table';
import { getNestedFieldName } from 'shared/utility';
import { FormattedNumber } from './formatted-number';

interface MoneyOrPercentFieldProps<T extends Record<string, any>> {
  original: T;
  index: number;
  id: IdType<T>;
  rules?: Object;
  percent?: boolean;
  section?: string;
  decimalScale?: number;
}
export function MoneyOrPercentField<T extends Record<string, any>>({
  index,
  id,
  rules,
  percent,
  section,
  decimalScale,
}: MoneyOrPercentFieldProps<T>) {
  const name = getNestedFieldName(section || 'noSectionConfigured')(index)(id);
  const {
    field: { onChange, ref, value },
    fieldState: { error },
  } = useController({
    name,
    rules,
  });

  const [defaultValue] = useState<typeof value>(value);
  // we use this because we need to multiply per 100 at the beginning or when the form is reseted
  const [isPercentageFirstTime, setIsPercentageFirstTime] = useState<boolean>(true);

  const fieldValue = useMemo(() => (isPercentageFirstTime && percent ? defaultValue * 100 : value), [
    isPercentageFirstTime,
    percent,
    value,
    defaultValue,
  ]);
  const firstValue = percent ? Number(defaultValue * 100).toFixed(3) : Number(defaultValue).toFixed(2);

  const setFixedValue = (n: number | string): string => {
    var result = Number(n) - Math.floor(Number(n)) !== 0;
    if (result) {
      return Number(n).toFixed(percent ? 3 : 2);
    } else {
      return Number(n).toString();
    }
  };

  useEffect(() => {
    if (percent && fieldValue === defaultValue) {
      setIsPercentageFirstTime(true);
    }
  }, [percent, fieldValue, defaultValue]);

  return (
    <Box>
      <FormattedNumber
        id={name}
        displayType='input'
        customInput={TextField}
        size='small'
        onBlur={(ev: any) => {
          const targetValue = percent
            ? ev.target.value.replace('%', '')
            : ev.target.value.replaceAll(',', '').replace('$', '');
          if (targetValue !== firstValue) {
            setIsPercentageFirstTime(false);
            if (fieldValue === '') {
              const newValue = percent ? 0.0 : 0.0;
              onChange(newValue);
              return;
            }
            // we call it onChange again here to avoid losing decimals
            // (there is something weird with the lib)
            onChange(setFixedValue(fieldValue));
          }
        }}
        onValueChange={v => {
          if (firstValue !== fieldValue) {
            setIsPercentageFirstTime(false);
            if (v.value === '') {
              onChange('');
              return;
            }
            const value = Number(v.floatValue || v.value);
            onChange(setFixedValue(value));
          }
        }}
        value={fieldValue}
        error={Boolean(error)}
        helperText={error?.message}
        inputRef={ref}
        inputProps={{
          'aria-label': name,
        }}
        variant='standard'
        prefix={percent ? undefined : '$'}
        suffix={percent ? '%' : undefined}
        fixedDecimalScale
        decimalScale={decimalScale}
      />
    </Box>
  );
}
